<script setup>
/**
 * RichTextField — Tiptap-backed rich-text editor, v-model<string> of HTML.
 */
import { ref, watch, onBeforeUnmount } from "vue";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";

const props = defineProps({
  modelValue:    { type: String,   default: "" },
  label:         { type: String,   default: "" },
  imageUploader: { type: Function, default: null },
});
const emit = defineEmits(["update:modelValue"]);

const imageInput = ref(null);
const inserting = ref(false);

const editor = useEditor({
  content: props.modelValue,
  extensions: [StarterKit, Link.configure({ openOnClick: false, autolink: true }), Image],
  editorProps: { attributes: { class: "rte__content" } },
  onUpdate: ({ editor }) => emit("update:modelValue", editor.getHTML()),
});

watch(() => props.modelValue, (val) => {
  if (editor.value && val !== editor.value.getHTML()) {
    editor.value.commands.setContent(val || "", false);
  }
});

onBeforeUnmount(() => editor.value?.destroy());

const is = (name, attrs) => editor.value?.isActive(name, attrs);

function setLink() {
  const prev = editor.value.getAttributes("link").href;
  const url = window.prompt("Link URL", prev || "https://");
  if (url === null) return;
  if (url === "") { editor.value.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
  editor.value.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}

function pickImage() { imageInput.value?.click(); }
async function onImageSelected(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file || !props.imageUploader) return;
  inserting.value = true;
  try {
    const url = await props.imageUploader(file, () => {});
    if (url) editor.value.chain().focus().setImage({ src: url }).run();
  } catch { window.alert("Couldn't upload the image."); }
  finally { inserting.value = false; }
}
</script>

<template>
  <div class="rte">
    <div v-if="label" class="rte__label text-body-2 font-weight-semibold mb-1">{{ label }}</div>

    <div v-if="editor" class="rte__toolbar">
      <button type="button" class="rte__btn" :class="{ 'is-active': is('bold') }"           @click="editor.chain().focus().toggleBold().run()"><b>B</b></button>
      <button type="button" class="rte__btn" :class="{ 'is-active': is('italic') }"          @click="editor.chain().focus().toggleItalic().run()"><i>I</i></button>
      <span class="rte__sep" />
      <button type="button" class="rte__btn" :class="{ 'is-active': is('heading', { level: 2 }) }" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">H2</button>
      <button type="button" class="rte__btn" :class="{ 'is-active': is('heading', { level: 3 }) }" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">H3</button>
      <span class="rte__sep" />
      <button type="button" class="rte__btn" :class="{ 'is-active': is('bulletList') }"      @click="editor.chain().focus().toggleBulletList().run()">•</button>
      <button type="button" class="rte__btn" :class="{ 'is-active': is('orderedList') }"     @click="editor.chain().focus().toggleOrderedList().run()">1.</button>
      <span class="rte__sep" />
      <button type="button" class="rte__btn" :class="{ 'is-active': is('link') }"            @click="setLink">🔗</button>
      <button v-if="imageUploader" type="button" class="rte__btn" :disabled="inserting"      @click="pickImage">{{ inserting ? "…" : "🖼" }}</button>
    </div>

    <EditorContent :editor="editor" class="rte__editor" />
    <input ref="imageInput" type="file" accept="image/*" class="rte__file-input" @change="onImageSelected" />
  </div>
</template>

<style scoped>
.rte { display: flex; flex-direction: column; }
.rte__toolbar {
  display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
  padding: 6px 8px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: rgba(var(--v-theme-surface-variant), 0.4);
}
.rte__btn {
  min-width: 28px; height: 28px; padding: 0 6px;
  border: 1px solid transparent; border-radius: 4px;
  background: transparent; cursor: pointer; font-size: 0.9rem;
}
.rte__btn:hover { background: rgba(var(--v-border-color), 0.2); }
.rte__btn.is-active { background: rgb(var(--v-theme-primary)); color: rgb(var(--v-theme-on-primary)); }
.rte__btn:disabled { opacity: 0.5; cursor: default; }
.rte__sep { width: 1px; height: 18px; background: rgba(var(--v-border-color), var(--v-border-opacity)); margin: 0 4px; }
.rte__editor {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0 0 8px 8px;
}
.rte__file-input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
:deep(.rte__content) { min-height: 160px; padding: 12px; outline: none; }
:deep(.rte__content p) { margin: 0 0 8px; }
:deep(.rte__content img) { max-width: 100%; border-radius: 4px; }
:deep(.rte__content a) { color: rgb(var(--v-theme-primary)); }
</style>
